import React, { useContext, useState, useRef, useEffect, useCallback } from 'react';
import { ComponentContext } from '../../contexts';
import { COLORS, LABEL_LINE_HEIGHT, TEXT_FONT_SIZE, LABEL_FONT_FAMILY } from '../../constants';
import { hasVisibleText } from '../../utils/text';
import { KEYBOARD_KEY } from '../../interfaces';
import { getFontStyles, countTextRows } from './utils';
// Compensating padding introduced by font
// This ensures vertical centering inside text shape
const TEXT_VIEW_POSITIONING_FIX = 0.045;
// Line rendering between textarea and SVG differs
// This magical constant aligns them as much as possible
const TEXT_EDIT_POSITIONING_FIX = `0.0291em`;
const MOUSE_CLICK_SPEED = 200;
export const Text = ({ definition, editing = false, onChange, onBlur = null, onFocus = null, onClick = null, onMouseEnter = null, onMouseLeave = null, }) => {
    const { text: stateText, color = COLORS.TEXT, width, height } = definition;
    const [revertText, setRevertText] = useState(stateText);
    const { PointerArea } = useContext(ComponentContext);
    const textAreaRef = useRef();
    const mouseClickTimer = useRef(null);
    const mouseClicked = useRef(false);
    // This is needed to update text synchronously (prevent caret jump, etc.)
    const [text, setText] = useState(stateText);
    useEffect(() => {
        if (!editing) {
            setRevertText(stateText);
        }
    }, [editing, stateText]);
    useEffect(() => {
        if (editing) {
            const el = textAreaRef.current;
            const handleEscape = (e) => {
                if (e.key.toLowerCase() === KEYBOARD_KEY.ESCAPE) {
                    textAreaRef.current.blur();
                }
            };
            const handleEvent = (e) => {
                e.stopPropagation();
            };
            el.addEventListener('keyup', handleEscape);
            const handledEventTypes = ['keydown', 'keyup', 'mousedown', 'mouseup'];
            handledEventTypes.forEach(type => el.addEventListener(type, handleEvent));
            return () => {
                handledEventTypes.forEach(type => el.removeEventListener(type, handleEvent));
                el.removeEventListener('keyup', handleEscape);
            };
        }
        return () => { };
    }, [editing]);
    useEffect(() => {
        if (!editing) {
            setText(stateText);
        }
    }, [stateText, editing]);
    const handleChange = useCallback((e) => {
        const newText = e.target.value;
        if (newText.length) {
            onChange?.(e.target.value);
        }
        setText(newText);
    }, [onChange]);
    const handleMouseDown = useCallback(() => {
        clearTimeout(mouseClickTimer.current);
        mouseClicked.current = true;
        mouseClickTimer.current = setTimeout(() => {
            mouseClicked.current = false;
        }, MOUSE_CLICK_SPEED);
    }, []);
    const handleMouseUp = useCallback(() => {
        clearTimeout(mouseClickTimer.current);
        if (mouseClicked.current) {
            onClick?.();
        }
    }, [onClick]);
    const handleBlur = useCallback(() => {
        if (!hasVisibleText(text)) {
            onChange?.(revertText);
            setText(revertText);
        }
        onBlur?.();
    }, [onBlur, onChange, revertText, text]);
    const rows = countTextRows(text);
    const fontSize = definition.fontSize || TEXT_FONT_SIZE;
    return (React.createElement("svg", { onMouseDown: handleMouseDown, onMouseUp: handleMouseUp, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, "data-handles-events": true, y: -fontSize * TEXT_VIEW_POSITIONING_FIX },
        React.createElement(PointerArea, { width: width, height: height }),
        text.split('\n').map((line, index) => (React.createElement("text", { key: index, x: 0, y: index * fontSize * LABEL_LINE_HEIGHT, dominantBaseline: "text-before-edge", fill: editing ? COLORS.TRANSPARENT : color, fontFamily: LABEL_FONT_FAMILY, fontSize: `${definition.fontSize}px`, style: {
                userSelect: 'none',
            } }, line.replace(/ /g, '\xa0')))),
        React.createElement("foreignObject", { width: width, height: height, style: { pointerEvents: 'all', overflow: 'visible' } }, editing ? (React.createElement("textarea", { "data-not-bounding": true, ref: textAreaRef, rows: rows, onChange: handleChange, onFocus: onFocus, onBlur: handleBlur, style: {
                top: TEXT_EDIT_POSITIONING_FIX,
                position: 'absolute',
                backgroundColor: 'transparent',
                color,
                // Increased width ensures that when typing directly
                // into text shape it would resize without flickering
                width: width + fontSize * 2,
                padding: 0,
                margin: 0,
                resize: 'none',
                overflow: 'hidden',
                ...getFontStyles(definition),
            } }, text)) : null)));
};
//# sourceMappingURL=text.js.map